import cn from 'classnames';
import { MultiSelect, MultiSelectFilterEvent } from 'primereact/multiselect';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import AdvertiserFilterOptionTemplate from '@features/campaigns/list/components/Filters/AdvertiserFilterOptionTemplate/AdvertiserFilterOptionTemplate.tsx';
import { useAdvertiserFilterOptions } from '@features/campaigns/list/components/Filters/hooks/useAdvertiserFilterOptions.ts';
import PlaceholderWithBadge from '@features/campaigns/list/components/Filters/PlaceholderWithBadge/PlaceholderWithBadge.tsx';
import { LoadingItemTemplate } from '@features/campaigns/shared/components/FormFields/Dropdown/components/LoadingItemTemplate/LoadingItemTemplate';
import { i18nNameSpace } from '@shared/consts/i18n';

import filtersStyles from '../Filters.module.scss';

interface AdvertiserFilterProps {
  selectedValues: string[] | undefined;
  advertiserLabels?: Record<string, string>;
  onChange: (event: { value: string[]; advertiserLabels?: Record<string, string> }) => void;
}

function AdvertiserFilter({ selectedValues, advertiserLabels, onChange }: AdvertiserFilterProps) {
  const { t } = useTranslation(i18nNameSpace.CAMPAIGNS);
  const [filterValue, setFilterValue] = useState('');
  const hasPersistedResolvedLabelsRef = useRef(false);
  const {
    advertiserOptions,
    currentItemsCount,
    totalItems,
    isLoading,
    isFetchingNextPage,
    fetchNextPage,
    resolvedAdvertiserLabels,
  } = useAdvertiserFilterOptions(filterValue, selectedValues, advertiserLabels);

  const isMinimumOneAdvertiserChecked = selectedValues && selectedValues.length > 0;

  useEffect(() => {
    if (hasPersistedResolvedLabelsRef.current || !resolvedAdvertiserLabels || !selectedValues?.length) {
      return;
    }

    const hasNewResolvedLabels = selectedValues.some(
      (advertiserId) => resolvedAdvertiserLabels[advertiserId] && !advertiserLabels?.[advertiserId],
    );

    if (!hasNewResolvedLabels) {
      return;
    }

    hasPersistedResolvedLabelsRef.current = true;
    onChange({
      value: selectedValues,
      advertiserLabels: { ...advertiserLabels, ...resolvedAdvertiserLabels },
    });
  }, [advertiserLabels, onChange, resolvedAdvertiserLabels, selectedValues]);

  const handleOnFilter = (event: MultiSelectFilterEvent) => {
    setFilterValue(event.filter);
  };

  const handleOnChange = ({ value }: { value: string[] }) => {
    const nextAdvertiserLabels =
      value.length > 0
        ? Object.fromEntries(
            value.map((advertiserId) => [
              advertiserId,
              advertiserOptions.find((option) => option.value === advertiserId)?.label ??
                advertiserLabels?.[advertiserId] ??
                advertiserId,
            ]),
          )
        : undefined;

    onChange({ value, advertiserLabels: nextAdvertiserLabels });
  };

  return (
    <MultiSelect
      data-testid='campaign-advertiser-filter'
      className={cn(filtersStyles.filter, filtersStyles.multiSelect, filtersStyles.advertiserMultiSelect)}
      options={advertiserOptions}
      placeholder={t('page.list.columns.advertiser')}
      onChange={handleOnChange}
      value={selectedValues}
      maxSelectedLabels={advertiserOptions.length || 1}
      aria-label='advertiser filter'
      filter
      onFilter={handleOnFilter}
      onHide={() => setFilterValue('')}
      resetFilterOnHide
      showSelectAll={false}
      loading={isLoading}
      virtualScrollerOptions={
        process.env.NODE_ENV !== 'test'
          ? {
              lazy: true,
              step: 20,
              onLazyLoad: async (event) => {
                const canFetchNextPage = event.last === advertiserOptions.length && currentItemsCount < totalItems;

                if (canFetchNextPage) {
                  await fetchNextPage();
                }
              },
              itemSize: 38,
              showLoader: true,
              loading: isFetchingNextPage,
              loadingTemplate: <LoadingItemTemplate />,
            }
          : undefined
      }
      itemTemplate={AdvertiserFilterOptionTemplate}
      selectedItemTemplate={
        isMinimumOneAdvertiserChecked && (
          <PlaceholderWithBadge
            data={selectedValues}
            placeholder={t('page.list.columns.advertiser')}
            prefixId='advertiser'
          />
        )
      }
      panelClassName={cn(filtersStyles.panel, filtersStyles.advertiserPanel)}
    />
  );
}

export default AdvertiserFilter;
